import { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { TableProps } from './table';
import { RowViewModel } from '../viewModels/row';
import { TableViewModel } from '../viewModels/table';
import { TableManagerViewModel } from '../viewModels/manager';

export type TableManagerProps<
  TData extends object,
  TRowViewModel extends RowViewModel<TData, TTableViewModel>,
  TTableViewModel extends TableViewModel<TData, TRowViewModel>,
  TTableProps extends TableProps<TData, TRowViewModel, TTableViewModel>
> = Omit<TTableProps, 'viewModel'> & {
  Table: FC<TTableProps>;
  viewModel: TableManagerViewModel<TData, TRowViewModel, TTableViewModel>;
  onAllRowsSelected?: () => void;
};

export const TableManager = observer(
  <
    TData extends object,
    TRowViewModel extends RowViewModel<TData, TTableViewModel>,
    TTableViewModel extends TableViewModel<TData, TRowViewModel>,
    TTableProps extends TableProps<TData, TRowViewModel, TTableViewModel>
  >({
    Table,
    viewModel,
    ...tableProps
  }: TableManagerProps<TData, TRowViewModel, TTableViewModel, TTableProps>) => {
    return (
      // @ts-expect-error tableProps should match TTableProps, but is not recognised as such
      <Table
        {...tableProps}
        viewModel={viewModel.tableViewModel}
        pagination
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        loading={viewModel.loading.is}
        rowCount={viewModel.totalCount}
        page={viewModel.page}
        pageSize={viewModel.pageSize}
        onPageSizeChange={(size) => viewModel.onPageSizeChange(size)}
        onPageChange={(page) => {
          viewModel.onPageChange(page);
          const activeElement = document.activeElement;
          if (activeElement instanceof HTMLElement) {
            activeElement.blur();
          }
        }}
        onFilterModelChange={async (model, details) => {
          model = await viewModel.onFilterModelChange(model, details);
          tableProps.onFilterModelChange?.(model, details);
        }}
        onSortModelChange={(model, details) => {
          viewModel.onSortModelChange(model);
          tableProps.onSortModelChange?.(model, details);
        }}
        keepNonExistentRowsSelected
        selectionModel={viewModel.selectionModel}
        onSelectionModelChange={(m, d) => {
          viewModel.onSelectionModelChange(m);
          tableProps.onSelectionModelChange?.(m, d);
        }}
        additionalComponent={tableProps.additionalComponent}
        customTableRoofHeadline={
          tableProps.customTableRoofHeadline ?? viewModel.customTableRoofHeadline
        }
        getRowClassName={(params) => viewModel.getRowClassName(params)}
      />
    );
  }
);
